import { type Filters } from '../hooks/useFilters'
import {
  LabRequisition,
  LabRequisitions,
  Requisition,
  ResultSet,
  Sample,
  SampleStatusEnum,
  Substances
} from '../types/types'

export const MIN_HOURS_BEFORE_SENDING_REQUISITION = 24

export const getFirstRequisition = (
  sample: Sample | undefined
): Requisition | undefined => sample?.requisitions?.[0]

export const getRequisitionResultSets = (
  requisition: Requisition | LabRequisition
): ResultSet[] => {
  const results = requisition.results?.subSamples ?? {}
  return Object.values(results).flatMap((value) => value.resultSets || [])
}

export const getAttachmentsCount = (
  requisition: Requisition | LabRequisition
): number =>
  getRequisitionResultSets(requisition).reduce(
    (n, rs) => n + (rs?.attachments?.length ?? 0),
    0
  )

export const getRequisitionNumberOfResults = (
  requisition: Requisition | LabRequisition
): number => getRequisitionResultSets(requisition)?.length || 0

export const getRequisitionNumberOfSubstances = (
  requisition?: Requisition
): number => requisition?.analysisType.substances?.length ?? 0

export const getRequisitionNumberOfResultsMissing = (
  requisition: Requisition
): number =>
  Math.max(
    getRequisitionNumberOfSubstances(requisition) -
      getRequisitionNumberOfResults(requisition),
    0
  )

export const isRequisitionContainingAnyResults = (
  requisition: Requisition | LabRequisition
): boolean => getRequisitionNumberOfResults(requisition) > 0

export const isFollowUpNull = (requisition: Requisition | undefined): boolean =>
  !requisition || !('followUp' in requisition) || requisition.followUp == null

export enum RequisitionStatus {
  no_reports_uploaded = 'no_reports_uploaded',
  reports_uploaded = 'reports_uploaded',
  completed = 'completed',
  compromised = 'compromised',
  reports_pending = 'reports_pending',
  reports_upload_failed = 'reports_upload_failed'
}

export const getRequisitionStatusForLab = (
  requisition: LabRequisition
): RequisitionStatus => {
  if (requisition.status === 'Compromised') {
    return RequisitionStatus.compromised
  }
  const resultSets = getRequisitionResultSets(requisition)
  if (resultSets.some((result) => result.reportUploadState === 'UPLOAD_FAILED'))
    return RequisitionStatus.reports_upload_failed
  if (resultSets.some((result) => result.reportUploadState === 'PENDING'))
    return RequisitionStatus.reports_pending
  if (
    (requisition.status as SampleStatusEnum) === 'ReadyToProcess' ||
    (requisition.status as SampleStatusEnum) === 'Completed'
  ) {
    return RequisitionStatus.completed
  }
  const hasAnyResults = getAttachmentsCount(requisition) > 0
  if (hasAnyResults) {
    return RequisitionStatus.reports_uploaded
  } else {
    return RequisitionStatus.no_reports_uploaded
  }
}

export const getRequisitionStatusTextForLab = (
  status: RequisitionStatus
): string => {
  switch (status) {
    case RequisitionStatus.no_reports_uploaded:
      return 'No reports uploaded'
    case RequisitionStatus.reports_uploaded:
      return 'Report(s) uploaded'
    case RequisitionStatus.completed:
      return 'Analysis reported complete'
    case RequisitionStatus.compromised:
      return 'Dismissed'
    case RequisitionStatus.reports_pending:
      return 'Venter på sikkerhetskontroll – rapport ikke tilgjengelig'
    case RequisitionStatus.reports_upload_failed:
      return 'Opplasting mislyktes – last opp samme fil på nytt'
  }
}

export const filterRequisitionList = (
  requisitions: LabRequisitions,
  filters: Filters<RequisitionStatus>
): LabRequisitions => {
  return requisitions.filter((requisition) => {
    const sampleId = requisition.sampleId
    const status = getRequisitionStatusForLab(requisition)
    const substanceIds =
      requisition.substances?.map((substance) => substance.id.toString()) ?? []
    return (
      (filters.searchText == null ||
        filters.searchText.length === 0 ||
        sampleId?.startsWith(filters.searchText)) &&
      (filters.status == null ||
        filters.status.length === 0 ||
        filters.status.includes(status)) &&
      (filters.substanceIds == null ||
        filters.substanceIds.every((value) => substanceIds.includes(value)))
    )
  })
}

export const formatSubstanceNames = (
  substances?: Substances,
  separator = ', '
): string | undefined =>
  substances
    ?.map((substance) => substance?.name)
    .sort()
    .join(separator)
